<template>
  <div ref="root" class="relative w-full">
    <button
      type="button"
      class="flex h-10 w-full items-center justify-between gap-3 rounded-md border border-land-border bg-white px-3 text-left text-sm text-land-ink shadow-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20 disabled:cursor-not-allowed disabled:bg-land-surface-muted disabled:text-land-ink-soft"
      :class="open ? 'border-land-primary ring-2 ring-land-primary/20' : ''"
      :aria-expanded="open"
      :aria-label="ariaLabel || placeholder"
      :data-testid="testId"
      aria-haspopup="listbox"
      :disabled="disabled"
      @click="toggleMenu"
      @keydown="handleButtonKeydown"
    >
      <span class="truncate" :class="selectedOption ? 'text-land-ink' : 'text-land-ink-soft'">{{ selectedLabel }}</span>
      <ChevronDown class="size-4 shrink-0 text-land-ink-soft transition-transform" :class="open ? 'rotate-180' : ''" aria-hidden="true" />
    </button>

    <div
      v-if="open"
      class="absolute left-0 right-0 top-[calc(100%+0.25rem)] z-[100] max-h-64 overflow-y-auto rounded-md border border-land-border bg-white py-1 text-sm shadow-xl"
      role="listbox"
      :aria-label="ariaLabel || placeholder"
    >
      <button
        v-for="(option, index) in normalizedOptions"
        :key="`${option.value}-${index}`"
        type="button"
        class="flex min-h-9 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
        :class="optionClasses(option, index)"
        :aria-selected="isSelected(option)"
        :disabled="option.disabled"
        role="option"
        @mouseenter="activeIndex = index"
        @click="selectOption(option)"
      >
        <span class="truncate">{{ option.label }}</span>
        <Check v-if="isSelected(option)" class="size-4 shrink-0" aria-hidden="true" />
      </button>
      <p v-if="normalizedOptions.length === 0" class="px-3 py-2 text-sm text-land-ink-soft">No options</p>
    </div>
  </div>
</template>

<script setup>
import { Check, ChevronDown } from '@lucide/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  modelValue: {
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select'
  },
  ariaLabel: {
    type: String,
    default: ''
  },
  testId: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const root = ref(null)
const open = ref(false)
const activeIndex = ref(-1)

const normalizedOptions = computed(() => props.options.map((option) => {
  if (typeof option === 'string' || typeof option === 'number') {
    return { value: option, label: String(option), disabled: false }
  }

  return {
    value: option.value,
    label: option.label || String(option.value || ''),
    disabled: Boolean(option.disabled)
  }
}))

const selectedOption = computed(() => normalizedOptions.value.find((option) => isSelected(option)))
const selectedLabel = computed(() => selectedOption.value?.label || props.placeholder)

const isSelected = (option) => String(option.value ?? '') === String(props.modelValue ?? '')

const toggleMenu = () => {
  if (props.disabled) return

  open.value ? closeMenu() : openMenu()
}

const openMenu = async () => {
  open.value = true
  activeIndex.value = selectedOptionIndex()
  if (activeIndex.value < 0) activeIndex.value = firstEnabledIndex()

  await nextTick()
  root.value?.querySelector('[role="listbox"]')?.scrollIntoView({ block: 'nearest' })
}

const closeMenu = () => {
  open.value = false
}

const selectedOptionIndex = () => normalizedOptions.value.findIndex((option) => isSelected(option))
const firstEnabledIndex = () => normalizedOptions.value.findIndex((option) => !option.disabled)

const moveActive = (direction) => {
  if (normalizedOptions.value.length === 0) return

  let nextIndex = activeIndex.value

  for (let step = 0; step < normalizedOptions.value.length; step += 1) {
    nextIndex = (nextIndex + direction + normalizedOptions.value.length) % normalizedOptions.value.length
    if (!normalizedOptions.value[nextIndex].disabled) {
      activeIndex.value = nextIndex
      return
    }
  }
}

const selectOption = (option) => {
  if (option.disabled) return

  emit('update:modelValue', option.value)
  emit('change', option.value)
  closeMenu()
}

const handleButtonKeydown = (event) => {
  if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Escape', 'Home', 'End'].includes(event.key)) {
    event.preventDefault()
  }

  if (event.key === 'ArrowDown') {
    if (!open.value) openMenu()
    moveActive(1)
  } else if (event.key === 'ArrowUp') {
    if (!open.value) openMenu()
    moveActive(-1)
  } else if (event.key === 'Home') {
    openMenu()
    activeIndex.value = firstEnabledIndex()
  } else if (event.key === 'End') {
    openMenu()
    activeIndex.value = [...normalizedOptions.value].reverse().findIndex((option) => !option.disabled)
    if (activeIndex.value >= 0) activeIndex.value = normalizedOptions.value.length - activeIndex.value - 1
  } else if (event.key === 'Enter' || event.key === ' ') {
    if (!open.value) {
      openMenu()
      return
    }

    const option = normalizedOptions.value[activeIndex.value]
    if (option) selectOption(option)
  } else if (event.key === 'Escape') {
    closeMenu()
  }
}

const optionClasses = (option, index) => {
  if (option.disabled) return 'cursor-not-allowed text-land-ink-soft'
  if (isSelected(option)) return 'bg-land-primary-soft font-semibold text-land-primary'
  if (index === activeIndex.value) return 'bg-land-surface-muted text-land-ink'

  return 'text-land-ink-muted hover:bg-land-surface-muted hover:text-land-ink'
}

const handlePointerDown = (event) => {
  if (!root.value || root.value.contains(event.target)) return

  closeMenu()
}

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handlePointerDown)
})
</script>
